import { FC, useState, useEffect } from 'react';
import { Container } from '@/components/ui/Container';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { AIApp, AppType } from '@/types';

// 重用原来的AI应用组件内容
export const AppsPage: FC = () => {
  const [apps, setApps] = useState<AIApp[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 获取激活的AI应用列表
  const fetchApps = async () => {
    try {
      setLoading(true);
      setError(null);

      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';

      // 从公开API接口获取激活的应用列表
      const response = await fetch(`${API_BASE_URL}/api/v1/public/apps?page=1&size=20`);
      const data = await response.json();

      if (data.code === 200) {
        // 如果数据少于12个，尝试获取推荐应用补充
        let allApps = data.data.apps || [];

        if (allApps.length < 12) {
          try {
            const featuredResponse = await fetch(`${API_BASE_URL}/api/v1/public/apps/featured?limit=12`);
            const featuredData = await featuredResponse.json();

            if (featuredData.code === 200 && featuredData.data) {
              const featuredApps = featuredData.data.filter((app: AIApp) =>
                !allApps.some((existing: AIApp) => existing.id === app.id)
              );
              allApps = [...allApps, ...featuredApps];
            }
          } catch (featuredError) {
            console.warn('Failed to fetch featured apps:', featuredError);
          }
        }

        // 如果还是不够，再尝试获取热门应用
        if (allApps.length < 12) {
          try {
            const hotResponse = await fetch(`${API_BASE_URL}/api/v1/public/apps/hot?limit=12`);
            const hotData = await hotResponse.json();

            if (hotData.code === 200 && hotData.data) {
              const hotApps = hotData.data.filter((app: AIApp) =>
                !allApps.some((existing: AIApp) => existing.id === app.id)
              );
              allApps = [...allApps, ...hotApps];
            }
          } catch (hotError) {
            console.warn('Failed to fetch hot apps:', hotError);
          }
        }

        setApps(allApps.slice(0, 20)); // 最多显示20个应用
      } else {
        throw new Error(data.message || '获取应用列表失败');
      }
    } catch (err) {
      console.error('Failed to fetch AI apps:', err);
      setError('加载应用列表失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApps();
  }, []);

  // 处理应用点击事件
  const handleAppClick = async (app: AIApp) => {
    try {
      if (app.app_type === 'android') {
        // 安卓应用：直接使用下载链接
        const downloadUrl = app.download_url;
        if (!downloadUrl) {
          throw new Error('下载链接不存在');
        }

        // 创建下载链接并触发下载
        const link = document.createElement('a');
        link.href = downloadUrl;

        // 从URL中提取原始文件名，如果没有则使用应用名
        let fileName = '';
        try {
          const urlParts = downloadUrl.split('/');
          const lastPart = urlParts[urlParts.length - 1];
          // 检查最后一部分是否包含.apk后缀
          if (lastPart.toLowerCase().includes('.apk')) {
            fileName = lastPart;
          } else {
            // 如果URL中不包含.apk，使用应用名加上.apk
            fileName = (app.name || 'app').replace(/[^a-zA-Z0-9\u4e00-\u9fa5]/g, '_') + '.apk';
          }
        } catch (e) {
          fileName = (app.name || 'app').replace(/[^a-zA-Z0-9\u4e00-\u9fa5]/g, '_') + '.apk';
        }

        link.download = fileName;

        // 添加到DOM并触发点击
        document.body.appendChild(link);
        link.click();

        // 延迟移除元素，确保下载开始
        setTimeout(() => {
          document.body.removeChild(link);
        }, 100);

        // 异步调用统计下载次数（不阻塞用户下载）
        try {
          const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';
          await fetch(`${API_BASE_URL}/api/v1/apps/${app.id}/download`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            }
          });
        } catch (statError) {
          console.warn('Failed to record download count:', statError);
          // 不影响用户下载体验
        }
      } else if (app.app_type === 'web') {
        // 网站工具：打开外部链接
        const url = app.external_url || app.download_url;
        if (url) {
          window.open(url, '_blank', 'noopener,noreferrer');
        } else {
          throw new Error('应用链接不存在');
        }
      }
    } catch (err) {
      console.error('Failed to open app:', err);
      alert('打开应用失败，请稍后重试');
    }
  };

  // 获取应用类型标签文本
  const getTypeText = (type: AppType) => {
    const typeMap = {
      android: '安卓应用',
      web: '网站工具'
    };
    return typeMap[type] || type;
  };

  // 获取操作按钮文本
  const getActionText = (type: AppType) => {
    const actionMap = {
      android: '下载',
      web: '访问'
    };
    return actionMap[type] || '打开';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <Container>
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              AI应用市场
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              精选AI工具应用，提升您的工作效率
            </p>
          </div>
          <div className="flex justify-center">
            <div className="animate-pulse space-y-4 w-full max-w-6xl">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {[...Array(16)].map((_, i) => (
                  <div key={i} className="bg-white rounded-lg p-6 border border-gray-200">
                    <div className="h-32 bg-gray-200 rounded-lg mb-4"></div>
                    <div className="h-6 bg-gray-200 rounded mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded mb-4"></div>
                    <div className="h-8 bg-gray-200 rounded"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  if (error || apps.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <Container>
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              AI应用市场
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              精选AI工具应用，提升您的工作效率
            </p>
          </div>

          <div className="text-center py-12">
            <div className="text-gray-500 mb-4">
              {error || '暂无可用应用'}
            </div>
            <Button onClick={fetchApps} variant="secondary">
              重新加载
            </Button>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <Container>
        {/* 页面标题 */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            AI应用市场
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            精选AI工具应用，提升您的工作效率
          </p>
          <div className="flex justify-center gap-4 flex-wrap">
            <div className="inline-flex items-center bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-medium">
              🌟 推荐应用
            </div>
            <div className="inline-flex items-center bg-red-100 text-red-800 px-4 py-2 rounded-full text-sm font-medium">
              🔥 热门工具
            </div>
            <div className="inline-flex items-center bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-medium">
              📱 安卓应用 & 网站工具
            </div>
          </div>
        </div>

        {/* 应用列表 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-12">
          {apps.map((app) => (
            <Card key={app.id} className="overflow-hidden hover:shadow-lg transition-shadow duration-300">
              {/* 应用图标 */}
              <div className="p-6 pb-0">
                <div className="flex items-center justify-center mb-4">
                  {app.icon_url ? (
                    <img
                      src={app.icon_url}
                      alt={app.name}
                      className="w-20 h-20 rounded-xl object-cover border border-gray-200"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHZpZXdCb3g9IjAgMCA4MCA4MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjgwIiBoZWlnaHQ9IjgwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik00MCA1MEMzMi4yNjggNTAgMjUgNDIuNzMyIDI1IDM1QzI1IDI3LjI2OCAzMi4yNjggMjAgNDAgMjBDNDcuNzMyIDIwIDU1IDI3LjI2OCA1NSAzNUM1NSA0Mi43MzIgNDcuNzMyIDUwIDQwIDUwWiIgZmlsbD0iIzlDQTNBRiIvPgo8Y2lyY2xlIGN4PSIzMCIgY3k9IjMwIiByPSIyIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik00MCA0MEMzNy43OTA5IDQwIDM2IDM4LjIwOTEgMzYgMzZDMzYgMzMuNzkwOSAzNy43OTA5IDMyIDQwIDMyQzQyLjIwOTEgMzIgNDQgMzMuNzkwOSA0NCAzNkM0NCAzOC4yMDkxIDQyLjIwOTEgNDAgNDAgNDBaIiBmaWxsPSIjRjNGNEY2Ii8+Cjwvc3ZnPgo=';
                      }}
                    />
                  ) : (
                    <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center text-white text-3xl font-bold">
                      {app.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>

                {/* 应用信息 */}
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900 truncate">
                      {app.name}
                    </h3>
                    {app.is_featured && (
                      <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                        推荐
                      </span>
                    )}
                    {app.is_hot && (
                      <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">
                        热门
                      </span>
                    )}
                  </div>

                  <div className="flex items-center justify-center gap-2 mb-3">
                    <span className={`px-2 py-1 text-xs rounded ${
                      app.app_type === 'android'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-purple-100 text-purple-800'
                    }`}>
                      {getTypeText(app.app_type)}
                    </span>
                    {app.category && (
                      <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded">
                        {app.category}
                      </span>
                    )}
                  </div>

                  <p className="text-gray-600 text-sm mb-4 line-clamp-2 h-10">
                    {app.description || '暂无描述'}
                  </p>

                  {/* 统计信息 */}
                  <div className="flex justify-center gap-4 text-xs text-gray-500 mb-4">
                    {app.download_count > 0 && (
                      <span>📥 {app.download_count.toLocaleString()}</span>
                    )}
                    {app.view_count > 0 && (
                      <span>👁️ {app.view_count.toLocaleString()}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* 操作按钮 */}
              <div className="px-6 pb-6">
                <Button
                  onClick={() => handleAppClick(app)}
                  className="w-full"
                  variant={app.app_type === 'android' ? 'primary' : 'secondary'}
                >
                  {getActionText(app.app_type)}
                </Button>
              </div>
            </Card>
          ))}
        </div>

        {/* 统计信息 */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center bg-gray-100 text-gray-700 px-6 py-3 rounded-lg">
            <span className="font-medium">共展示 {apps.length} 个AI应用</span>
          </div>
        </div>
      </Container>
    </div>
  );
};